import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { PDFDownloadLink } from '@react-pdf/renderer';
import html2pdf from 'html2pdf.js';
import Modal from '../components/ui/Modal';
import { apiService } from '../services/api';
import { formatPhoneNumber, getRelativeTime } from '../utils/formatters';
import { getApiErrorMessage, isImageFileName, isVideoFileName, createAttachmentObjectUrl } from '../utils/jobAttachments';
import TimelineEntryForm from '../components/timeline/TimelineEntryForm';
import { CreateJobContactEntryForm, JobContactEntry, EntryType, WorkOrder, JobContactAttachment, UpdateJobContactEntryDto } from '../types';
import { useAuth } from '../contexts/AuthContext';
import InvoicePage from '../components/invoice/InvoicePage';

// Import new components
import JobContactSheetHeader from '../components/workorder/JobContactSheetHeader';
import JobContactSheetTabs from '../components/workorder/JobContactSheetTabs';
import JobContactSidebar from '../components/workorder/JobContactSidebar';
import TimelineTab from '../components/workorder/TimelineTab';
import FilesTab from '../components/workorder/FilesTab';
import FinancialTab from '../components/workorder/FinancialTab';
import EditTimelineEntryModal from '../components/workorder/EditTimelineEntryModal';
import TimelineEntryHistoryModal from '../components/workorder/TimelineEntryHistoryModal';
import DeleteAttachmentModal from '../components/workorder/DeleteAttachmentModal';
import EmailWorkOrderModal from '../components/workorder/EmailWorkOrderModal';
import DocumentationOverrideModal from '../components/modals/DocumentationOverrideModal';
import { WorkOrderDocumentationChecklist } from '../types';

interface WorkOrderContactPageProps {}

const WorkOrderContactPage: React.FC<WorkOrderContactPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get the source page from query parameters
  const fromPage = searchParams.get('from') || 'workorders';
  
  // Set flag to restore state when navigating back
  React.useEffect(() => {
    console.log('WorkOrderContactPage - fromPage:', fromPage);
    if (fromPage === 'dashboard') {
      sessionStorage.setItem('restore-dashboard-state', 'true');
      console.log('Set restore-dashboard-state flag');
    } else if (fromPage === 'workorders') {
      sessionStorage.setItem('restore-workorders-state', 'true');
      console.log('Set restore-workorders-state flag');
    } else if (fromPage === 'calendar') {
      sessionStorage.setItem('restore-calendar-state', 'true');
      console.log('Set restore-calendar-state flag');
    }
  }, [fromPage]);
  
  // State for new entry form
  const [newEntryTypeId, setNewEntryTypeId] = useState<number>(1); // Default to Note (ID 1)
  const [newMessage, setNewMessage] = useState<string>('');
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  
  // State for add entry modal
  const [isAddEntryModalOpen, setIsAddEntryModalOpen] = useState(false);

  // State for edit entry modal
  const [isEditEntryOpen, setIsEditEntryOpen] = useState(false);
  const [entryToEdit, setEntryToEdit] = useState<JobContactEntry | null>(null);
  const [editedEntryIds, setEditedEntryIds] = useState<number[]>([]);

  // State for history modal
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedEntryForHistory, setSelectedEntryForHistory] = useState<JobContactEntry | null>(null);

  // State for timeline filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [entryTypeFilter, setEntryTypeFilter] = useState<number | null>(null);
  const [vendorFilter, setVendorFilter] = useState<string>('');

  // State for printing
  const [isPrinting, setIsPrinting] = useState(false);

  // State for image modal
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [currentImageName, setCurrentImageName] = useState<string>('');
  const [previewKind, setPreviewKind] = useState<'image' | 'video'>('image');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);

  // State for email modal
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  // Mutations for invoice status changes
  const markSentMutation = useMutation({
    mutationFn: (invoiceId: number) => apiService.markInvoiceAsSent(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrderInvoices', id] });
      toast.success('Invoice marked as sent');
    },
    onError: () => {
      toast.error('Failed to mark invoice as sent');
    }
  });

  const markPaidMutation = useMutation({
    mutationFn: (invoiceId: number) => apiService.markInvoiceAsPaid(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrderInvoices', id] });
      toast.success('Invoice marked as paid');
    },
    onError: () => {
      toast.error('Failed to mark invoice as paid');
    }
  });

  // State for checkboxes
  const [signedOff, setSignedOff] = useState(false);
  const [invoiced, setInvoiced] = useState(false);
  const [pricingReceived, setPricingReceived] = useState(false);
  const [photosReceived, setPhotosReceived] = useState(false);
  const [invoiceReceived, setInvoiceReceived] = useState(false);
  const [vendorPaid, setVendorPaid] = useState(false);
  const [vendorPaidDate, setVendorPaidDate] = useState<string | null>(null);
  const [vendorPaymentReference, setVendorPaymentReference] = useState('');

  // State for active tab
  const [activeTab, setActiveTab] = useState('timeline');

  // Confirm delete modal state
  const [isDeleteAttachmentOpen, setIsDeleteAttachmentOpen] = useState(false);
  const [attachmentPendingDelete, setAttachmentPendingDelete] = useState<JobContactAttachment | null>(null);

  // Documentation override modal state
  const [showDocumentationOverrideModal, setShowDocumentationOverrideModal] = useState(false);
  const [documentationWarning, setDocumentationWarning] = useState<WorkOrderDocumentationChecklist | null>(null);
  const [pendingStatusId, setPendingStatusId] = useState<number | null>(null);

  // Fetch work order data
  const { data: workOrderResponse, isLoading: workOrderLoading, error: workOrderError } = useQuery({
    queryKey: ['workOrder', id],
    queryFn: () => apiService.getWorkOrder(parseInt(id!)),
    enabled: !!id,
  });

  // Fetch contact entries
  const { data: contactEntriesResponse, isLoading: entriesLoading, refetch: refetchEntries } = useQuery({
    queryKey: ['contactEntries', id],
    queryFn: () => apiService.getJobContactEntries(parseInt(id!)),
    enabled: !!id,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Fetch statuses
  const { data: statusesResponse } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses(),
  });

  // Fetch entry types
  const { data: entryTypesResponse } = useQuery({
    queryKey: ['entryTypes'],
    queryFn: () => apiService.getEntryTypes(),
  });

  const workOrder = workOrderResponse?.data;

  // Determine default vendor for Vendor Message form
  const defaultVendorId = useMemo(() => {
    if (selectedVendorId) {
      const parsed = Number(selectedVendorId);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    const vendors = workOrder?.vendors || [];
    if (vendors.length === 1) {
      return vendors[0].vendorId;
    }
    return undefined;
  }, [selectedVendorId, workOrder]);
  
  const contactEntries = contactEntriesResponse?.data || [];
  const statuses = statusesResponse?.data || [];
  const entryTypes = entryTypesResponse?.data || [];

  // Fetch invoices for this work order to compute summary
  const { data: workOrderInvoices, isLoading: woInvoicesLoading } = useQuery({
    queryKey: ['workOrderInvoices', id],
    queryFn: () => apiService.getInvoicesForWorkOrder(parseInt(id!)),
    enabled: !!id,
    staleTime: 60_000
  });

  const invoiceSummary = useMemo(() => {
    const invoices = workOrderInvoices || [];
    const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const totalPaid = invoices
      .filter(inv => (inv.status || '').toLowerCase() === 'paid')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);
    const totalCredits = invoices
      .filter(inv => (inv.invoiceTypeName || '').toLowerCase() === 'credit')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);
    const outstanding = Math.max(0, totalInvoiced - totalPaid - Math.abs(totalCredits));
    return { count: invoices.length, totalInvoiced, totalPaid, totalCredits, outstanding };
  }, [workOrderInvoices]);

  // After entries load, probe which have history and mark them so badges render on first load
  React.useEffect(() => {
    const entries = contactEntriesResponse?.data || [];
    if (!entries.length) return;
    // Only check entries we haven't marked yet
    const toCheck = entries
      .filter((e: any) => !editedEntryIds.includes(e.id))
      .map((e: any) => e.id);
    if (!toCheck.length) return;
    let isCancelled = false;
    (async () => {
      try {
        const results = await Promise.allSettled(toCheck.map((idNum) => apiService.getJobContactEntryHistory(idNum)));
        const editedIds: number[] = [];
        results.forEach((res, idx) => {
          if (res.status === 'fulfilled' && Array.isArray(res.value?.data) && res.value.data.length > 0) {
            editedIds.push(toCheck[idx]);
          }
        });
        if (!isCancelled && editedIds.length) {
          setEditedEntryIds((prev) => Array.from(new Set([...prev, ...editedIds])));
        }
      } catch {}
    })();
    return () => {
      isCancelled = true;
    };
  }, [contactEntriesResponse, editedEntryIds]);

  // Initialize checkbox state when work order data is loaded
  React.useEffect(() => {
    if (workOrder) {
      setSignedOff(workOrder.signedOff);
      setInvoiced(workOrder.invoiced);
      setPricingReceived(workOrder.pricingReceived ?? false);
      setPhotosReceived(workOrder.photosReceived ?? false);
      setInvoiceReceived(workOrder.invoiceReceived ?? false);
      setVendorPaid(workOrder.vendorPaid ?? false);
      setVendorPaidDate(workOrder.vendorPaidDate ?? null);
      setVendorPaymentReference(workOrder.vendorPaymentReference ?? '');
    }
  }, [workOrder]);

  // Create new contact entry mutation
  const createEntryMutation = useMutation({
    mutationFn: ({
      data,
      onProgress,
    }: {
      data: CreateJobContactEntryForm;
      onProgress?: (percent: number) => void;
    }) => apiService.createJobContactEntry(parseInt(id!), data, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
      setIsAddEntryModalOpen(false);
      setNewMessage('');
      setAttachedFiles([]);
      setSelectedVendorId('');
      toast.success('Timeline entry added successfully');
    },
    onError: (error: unknown) => {
      console.error('Error creating contact entry:', error);
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({
      workOrderId,
      statusId,
      workOrderData,
      documentationOverride,
    }: {
      workOrderId: number;
      statusId: number;
      workOrderData: WorkOrder;
      documentationOverride?: {
        acknowledgeDocumentationWarning?: boolean;
        documentationOverrideReason?: string;
      };
    }) =>
      apiService.updateWorkOrderStatus(workOrderId, statusId, workOrderData, documentationOverride),
    onSuccess: (response) => {
      if (response.data?.requiresDocumentationAcknowledgment && response.data.documentationWarning) {
        setDocumentationWarning(response.data.documentationWarning);
        setShowDocumentationOverrideModal(true);
        return;
      }

      if (response.success && response.data?.updated !== false) {
        queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
        setShowDocumentationOverrideModal(false);
        setDocumentationWarning(null);
        setPendingStatusId(null);
        toast.success('Status updated successfully');
        return;
      }

      toast.error(response.message || 'Failed to update status');
    },
    onError: (error: any) => {
      console.error('Error updating status:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update status';
      toast.error(errorMessage);
      setPendingStatusId(null);
    },
  });

  // Update signed off mutation
  const updateSignedOffMutation = useMutation({
    mutationFn: (signedOff: boolean) => 
      apiService.updateWorkOrderSignedOff(parseInt(id!), signedOff),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Signed off status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating signed off:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update signed off status';
      toast.error(errorMessage);
    },
  });

  // Update invoiced mutation
  const updateInvoicedMutation = useMutation({
    mutationFn: (invoiced: boolean) => 
      apiService.updateWorkOrderInvoiced(parseInt(id!), invoiced),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Invoiced status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating invoiced:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update invoiced status';
      toast.error(errorMessage);
    },
  });

  // History query
  const { data: entryHistory } = useQuery({
    queryKey: ['entryHistory', selectedEntryForHistory?.id],
    queryFn: () => (selectedEntryForHistory ? apiService.getJobContactEntryHistory(selectedEntryForHistory.id) : Promise.resolve(null)),
    enabled: !!selectedEntryForHistory && isHistoryModalOpen,
  });

  // Update timeline entry mutation
  const updateEntryMutation = useMutation({
    mutationFn: ({ entryId, data }: { entryId: number; data: UpdateJobContactEntryDto }) =>
      apiService.updateJobContactEntry(parseInt(id!), entryId, data),
    onSuccess: (_resp, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
      setIsEditEntryOpen(false);
      setEntryToEdit(null);
      if (variables?.entryId) setEditedEntryIds((prev) => Array.from(new Set([...prev, variables.entryId])));
      toast.success('Timeline entry updated successfully');
    },
    onError: (error: unknown) => {
      console.error('Error updating entry:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update timeline entry'));
    },
  });

  // Update quote requested mutation
  const updateQuoteRequestedMutation = useMutation({
    mutationFn: (quoteRequested: boolean) => 
      apiService.updateWorkOrderQuoteRequested(parseInt(id!), quoteRequested),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Quote requested status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating quote requested:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update quote requested status';
      toast.error(errorMessage);
    },
  });

  // Update pricing received mutation
  const updatePricingReceivedMutation = useMutation({
    mutationFn: (pricingReceived: boolean) => 
      apiService.updateWorkOrderPricingReceived(parseInt(id!), pricingReceived),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Pricing received status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating pricing received:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update pricing received status';
      toast.error(errorMessage);
    },
  });

  // Update photos received mutation
  const updatePhotosReceivedMutation = useMutation({
    mutationFn: (photosReceived: boolean) => 
      apiService.updateWorkOrderPhotosReceived(parseInt(id!), photosReceived),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Photos received status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating photos received:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update photos received status';
      toast.error(errorMessage);
    },
  });

  // Update invoice received mutation
  const updateInvoiceReceivedMutation = useMutation({
    mutationFn: (invoiceReceived: boolean) => 
      apiService.updateWorkOrderInvoiceReceived(parseInt(id!), invoiceReceived),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Invoice received status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating invoice received:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update invoice received status';
      toast.error(errorMessage);
    },
  });

  // Update vendor paid mutation
  const updateVendorPaidMutation = useMutation({
    mutationFn: (payload: {
      vendorPaid: boolean;
      vendorPaidDate?: string | null;
      vendorPaymentReference?: string | null;
    }) => apiService.updateWorkOrderVendorPaid(parseInt(id!), payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Vendor paid status updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating vendor paid:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update vendor paid status';
      toast.error(errorMessage);
    },
  });

  const handleVendorPaidSave = (payload: {
    vendorPaid: boolean;
    vendorPaidDate?: string | null;
    vendorPaymentReference?: string | null;
  }) => {
    updateVendorPaidMutation.mutate(payload);
  };

  // Update NTE mutation
  const updateNteMutation = useMutation({
    mutationFn: (nteAmount: number | null) =>
      apiService.updateWorkOrderNte(parseInt(id!), nteAmount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('NTE amount updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating NTE:', error);
      toast.error('Failed to update NTE amount');
    },
  });

  const updateCostsMutation = useMutation({
    mutationFn: (costs: {
      incurredCost: number | null;
      incurredCostNote: string | null;
      proposedCost: number | null;
      actualCost: number | null;
    }) => apiService.updateWorkOrderCosts(parseInt(id!), costs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrder', id] });
      toast.success('Costs updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating costs:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update costs';
      toast.error(errorMessage);
    },
  });

  // Delete attachment mutation
  const deleteAttachmentMutation = useMutation({
    mutationFn: ({ attachmentId, deleteReason }: { attachmentId: number; deleteReason: string }) =>
      apiService.deleteAttachmentWithHistory(attachmentId, { deleteReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
      setIsDeleteAttachmentOpen(false);
      setAttachmentPendingDelete(null);
      toast.success('Attachment deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    },
  });

  // Filter timeline entries
  const filteredEntries = useMemo(() => {
    let filtered = contactEntries;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.message.toLowerCase().includes(searchLower) ||
        entry.userName?.toLowerCase().includes(searchLower) ||
        entry.entryTypeName.toLowerCase().includes(searchLower)
      );
    }

    if (entryTypeFilter) {
      filtered = filtered.filter(entry => entry.entryTypeId === entryTypeFilter);
    }

    if (vendorFilter) {
      filtered = filtered.filter(entry => entry.vendorId?.toString() === vendorFilter);
    }

    return filtered.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
  }, [contactEntries, searchTerm, entryTypeFilter, vendorFilter]);

  // Get all attachments from timeline entries
  const allAttachments = useMemo(() => {
    const attachments: JobContactAttachment[] = [];
    contactEntries.forEach(entry => {
      if (entry.attachments) {
        attachments.push(...entry.attachments);
      }
    });
    return attachments;
  }, [contactEntries]);

  // Format timestamp helper
  const formatTimestamp = useCallback((dateString: string) => {
    if (!dateString) {
      return { date: '', time: '', relative: '' };
    }
    // Ensure we interpret the server time as UTC. If it lacks a timezone, append 'Z'.
    const isoString = /Z|[\+\-]\d{2}:?\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
    const date = new Date(isoString);

    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      relative: getRelativeTime(date.toISOString())
    };
  }, []);

  // Event handlers
  const handleStatusUpdate = (statusId: string) => {
    if (!workOrder) {
      toast.error('Work order data not loaded');
      return;
    }

    const parsedStatusId = parseInt(statusId, 10);
    setPendingStatusId(parsedStatusId);

    updateStatusMutation.mutate({
      workOrderId: parseInt(id!),
      statusId: parsedStatusId,
      workOrderData: workOrder,
    });
  };

  const handleDocumentationOverrideConfirm = (reason: string) => {
    if (!workOrder || pendingStatusId == null) return;

    updateStatusMutation.mutate({
      workOrderId: parseInt(id!),
      statusId: pendingStatusId,
      workOrderData: workOrder,
      documentationOverride: {
        acknowledgeDocumentationWarning: true,
        documentationOverrideReason: reason,
      },
    });
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      // Get the work order HTML content and print it
      const response = await apiService.renderWorkOrder(parseInt(id!));
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          window.URL.revokeObjectURL(url);
        };
      }
    } catch (error: any) {
      console.error('Error printing work order:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to print work order';
      toast.error(`Error printing work order: ${errorMessage}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleCreateDepositInvoice = async () => {
    try {
      console.log('Creating deposit invoice for work order:', id);
      const inv = await apiService.createDepositInvoice(parseInt(id!), 50);
      console.log('Deposit invoice created:', inv);
      toast.success(`Deposit invoice ${inv.invoiceNumber} created`);
      navigate(`/invoices/${inv.id}/edit`);
    } catch (e: any) {
      console.error('Failed to create deposit invoice:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to create deposit invoice';
      toast.error(errorMessage);
    }
  };

  const handleCreateProgressInvoice = async () => {
    try {
      console.log('Creating progress invoice for work order:', id);
      const inv = await apiService.createProgressInvoice(parseInt(id!), 'Progress billing');
      console.log('Progress invoice created:', inv);
      toast.success(`Progress invoice ${inv.invoiceNumber} created`);
      navigate(`/invoices/${inv.id}/edit`);
    } catch (e: any) {
      console.error('Failed to create progress invoice:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to create progress invoice';
      toast.error(errorMessage);
    }
  };

  const handleCreateFinalInvoice = async () => {
    try {
      console.log('Creating final invoice for work order:', id);
      const inv = await apiService.createFinalInvoice(parseInt(id!));
      console.log('Final invoice created:', inv);
      toast.success(`Final invoice ${inv.invoiceNumber} created`);
      navigate(`/invoices/${inv.id}/edit`);
    } catch (e: any) {
      console.error('Failed to create final invoice:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to create final invoice';
      toast.error(errorMessage);
    }
  };

  const handleCreateChangeOrderInvoice = async () => {
    try {
      console.log('Creating change order invoice for work order:', id);
      const inv = await apiService.createChangeOrderInvoice(parseInt(id!), 'Change order');
      console.log('Change order invoice created:', inv);
      toast.success(`Change Order invoice ${inv.invoiceNumber} created`);
      navigate(`/invoices/${inv.id}/edit`);
    } catch (e: any) {
      console.error('Failed to create change order invoice:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to create change order invoice';
      toast.error(errorMessage);
    }
  };

  const handleEmailClient = () => {
    setIsEmailModalOpen(true);
  };

  const handleExportPDF = async () => {
    try {
      // Get the work order HTML content
      const response = await apiService.renderWorkOrder(parseInt(id!));
      const htmlContent = response.data;
      
      // Create a temporary div that's visible but off-screen
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-2000px';
      tempDiv.style.top = '0px';
      tempDiv.style.width = '8.5in';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.zIndex = '-1';
      
      tempDiv.innerHTML = htmlContent;
      
      // Add to DOM temporarily
      document.body.appendChild(tempDiv);
      
      try {
        // Wait for images and content to load
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force a reflow to ensure all styles are applied
        const _ = tempDiv.offsetHeight; // Force reflow
        
        // Use html2pdf with optimized options
        const workOrderNumber = workOrder?.workOrderNumber || id;
        const opt = {
          margin: 0.5,
          filename: `workorder-${workOrderNumber}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: tempDiv.scrollWidth,
            height: tempDiv.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            windowWidth: tempDiv.scrollWidth,
            windowHeight: tempDiv.scrollHeight
          },
          jsPDF: { 
            unit: 'in', 
            format: 'letter', 
            orientation: 'portrait' 
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };
        
        // Clone the element with clean positioning for better html2canvas compatibility
        const clonedDiv = tempDiv.cloneNode(true) as HTMLElement;
        clonedDiv.style.position = 'static';
        clonedDiv.style.left = 'auto';
        clonedDiv.style.top = 'auto';
        clonedDiv.style.zIndex = 'auto';
        
        await html2pdf().set(opt).from(clonedDiv).save();
        toast.success('PDF exported successfully');
      } finally {
        // Clean up - remove the temporary div
        document.body.removeChild(tempDiv);
      }
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to export PDF';
      toast.error(`Error exporting PDF: ${errorMessage}`);
    }
  };

  const handleEditEntry = useCallback((entry: JobContactEntry) => {
    setEntryToEdit(entry);
    setIsEditEntryOpen(true);
  }, []);

  const handleViewHistory = useCallback((entry: JobContactEntry) => {
    setSelectedEntryForHistory(entry);
    setIsHistoryModalOpen(true);
  }, []);

  const handleDeleteWorkOrder = () => {
    // TODO: Implement delete work order functionality
    toast('Delete work order functionality coming soon', { icon: 'ℹ️' });
  };

  const openAddEntryModal = (defaultEntryTypeId?: number) => {
    setNewEntryTypeId(defaultEntryTypeId ?? 1); // 1 = Note, 4 = File Upload
    setIsAddEntryModalOpen(true);
  };

  const handleFileUpload = () => {
    openAddEntryModal(4); // File Upload entry type
  };

  // Download by path (used by Timeline tab)
  const handleFileDownload = useCallback(async (filePath: string, fileName: string) => {
    try {
      const blob = await apiService.downloadFile(filePath);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
      console.error('Error downloading file:', error);
    }
  }, []);

  // Download by attachment (used by Files tab)
  const handleFileDownloadAttachment = useCallback(async (attachment: JobContactAttachment) => {
    await handleFileDownload(attachment.filePath, attachment.fileName);
  }, [handleFileDownload]);

  // Preview in modal (used by Files tab)
  const handleFilePreview = useCallback(async (attachment: JobContactAttachment) => {
    try {
      const isImage = attachment.fileType?.startsWith('image') || isImageFileName(attachment.fileName);
      const isVideo = attachment.fileType?.startsWith('video') || isVideoFileName(attachment.fileName);
      if (!isImage && !isVideo) {
        await handleFileDownloadAttachment(attachment);
        return;
      }
      const blob = await apiService.downloadFile(attachment.filePath);
      const url = createAttachmentObjectUrl(blob, attachment.fileName);
      setPreviewKind(isVideo ? 'video' : 'image');
      setCurrentImageUrl(url);
      setCurrentImageName(attachment.fileName);
      setImageModalOpen(true);
    } catch (error) {
      toast.error('Failed to preview file');
      console.error('Error previewing file:', error);
    }
  }, [handleFileDownloadAttachment]);

  const handleViewImage = useCallback(async (filePath: string, fileName: string) => {
    try {
      const blob = await apiService.downloadFile(filePath);
      const url = createAttachmentObjectUrl(blob, fileName);
      setPreviewKind(isVideoFileName(fileName) ? 'video' : 'image');
      setCurrentImageUrl(url);
      setCurrentImageName(fileName);
      setImageModalOpen(true);
    } catch (error) {
      toast.error('Failed to load file');
      console.error('Error loading file:', error);
    }
  }, []);

  // Open confirmation (used by Timeline tab delete)
  const handleTimelineAttachmentDelete = useCallback((attachment: JobContactAttachment) => {
    setAttachmentPendingDelete(attachment);
    setIsDeleteAttachmentOpen(true);
  }, []);

  // Open confirmation (used by Files tab delete)
  const handleFileDeleteAttachment = useCallback((attachment: JobContactAttachment) => {
    setAttachmentPendingDelete(attachment);
    setIsDeleteAttachmentOpen(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setImageModalOpen(false);
    if (currentImageUrl) {
      window.URL.revokeObjectURL(currentImageUrl);
      setCurrentImageUrl('');
    }
    setCurrentImageName('');
    setPreviewKind('image');
  }, [currentImageUrl]);

  const handleViewInvoice = (invoiceId: number) => {
    const invoice = workOrderInvoices?.find(inv => inv.id === invoiceId);
    if (invoice) {
      setSelectedInvoice(invoice);
      setShowInvoiceDetails(true);
      return;
    }

    navigate(`/invoices/${invoiceId}/edit`);
  };


  // Loading state
  if (workOrderLoading || entriesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-color mx-auto mb-4"></div>
          <div className="text-gray-600 dark:text-gray-400">Loading work order...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (workOrderError || !workOrder) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Error loading work order</div>
        <div className="text-gray-500 mt-2">Please try again</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <JobContactSheetHeader
        workOrder={workOrder}
        statuses={statuses}
        fromPage={fromPage}
        onStatusUpdate={handleStatusUpdate}
        onPrint={handlePrint}
        onCreateDepositInvoice={handleCreateDepositInvoice}
        onCreateProgressInvoice={handleCreateProgressInvoice}
        onCreateFinalInvoice={handleCreateFinalInvoice}
        onCreateChangeOrderInvoice={handleCreateChangeOrderInvoice}
        onEmailClient={handleEmailClient}
        onExportPDF={handleExportPDF}
        onDeleteWorkOrder={handleDeleteWorkOrder}
      />

      {/* Tab Navigation */}
      <JobContactSheetTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        timelineCount={filteredEntries.length}
        filesCount={allAttachments.length}
      />

      {/* Main Content Area */}
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-80px)]">
        {/* Left Content Area */}
        <div className="flex-1 lg:overflow-y-auto">
          <div className="p-6">
            {activeTab === 'timeline' && (
              <TimelineTab
                entries={filteredEntries}
                entryTypes={entryTypes}
                workOrder={workOrder}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                entryTypeFilter={entryTypeFilter}
                setEntryTypeFilter={setEntryTypeFilter}
                vendorFilter={vendorFilter}
                setVendorFilter={setVendorFilter}
                onAddEntry={openAddEntryModal}
                formatTimestamp={formatTimestamp}
                onFileDownload={handleFileDownload}
                onViewImage={handleViewImage}
                onDeleteAttachment={handleTimelineAttachmentDelete}
                onEditEntry={handleEditEntry}
                onViewHistory={handleViewHistory}
                editedEntryIds={editedEntryIds}
              />
            )}
            
            {activeTab === 'files' && (
              <FilesTab
                attachments={allAttachments}
                onUpload={handleFileUpload}
                onDownload={handleFileDownloadAttachment}
                onPreview={handleFilePreview}
                onDelete={handleFileDeleteAttachment}
              />
            )}
            
            {activeTab === 'financial' && (
              <FinancialTab
                workOrderId={parseInt(id!, 10)}
                vendors={workOrder.vendors || []}
                defaultClientEmail={workOrder.clientLocationContactEmail || workOrder.clientEmail}
                invoices={workOrderInvoices || []}
                invoiceSummary={invoiceSummary}
                nteAmount={workOrder.nteAmount}
                statusName={workOrder.statusName}
                documentationChecklist={workOrder.documentationChecklist}
                onCreateDepositInvoice={handleCreateDepositInvoice}
                onCreateProgressInvoice={handleCreateProgressInvoice}
                onCreateFinalInvoice={handleCreateFinalInvoice}
                onCreateChangeOrderInvoice={handleCreateChangeOrderInvoice}
                onViewInvoice={handleViewInvoice}
              />
              )}
            </div>
          </div>

        {/* Right Sidebar */}
        <JobContactSidebar
          workOrder={workOrder}
          signedOff={signedOff}
          invoiced={invoiced}
          quoteRequested={workOrder?.quoteRequested ?? false}
          pricingReceived={pricingReceived}
          photosReceived={photosReceived}
          invoiceReceived={invoiceReceived}
          vendorPaid={vendorPaid}
          vendorPaidDate={vendorPaidDate}
          vendorPaymentReference={vendorPaymentReference}
          nteAmount={workOrder.nteAmount}
          incurredCost={workOrder.incurredCost}
          incurredCostNote={workOrder.incurredCostNote}
          proposedCost={workOrder.proposedCost}
          actualCost={workOrder.actualCost}
          onSignedOffChange={(checked) => {
                      setSignedOff(checked);
                      updateSignedOffMutation.mutate(checked);
                    }}
          onInvoicedChange={(checked) => {
                      setInvoiced(checked);
                      updateInvoicedMutation.mutate(checked);
                    }}
          onQuoteRequestedChange={(checked) => {
                      updateQuoteRequestedMutation.mutate(!!checked);
                    }}
          onPricingReceivedChange={(checked) => {
                      setPricingReceived(checked);
                      updatePricingReceivedMutation.mutate(checked);
                    }}
          onPhotosReceivedChange={(checked) => {
                      setPhotosReceived(checked);
                      updatePhotosReceivedMutation.mutate(checked);
                    }}
          onInvoiceReceivedChange={(checked) => {
                      setInvoiceReceived(checked);
                      updateInvoiceReceivedMutation.mutate(checked);
                    }}
          onVendorPaidChange={(checked) => {
                      const nextDate = checked ? (vendorPaidDate ?? new Date().toISOString()) : null;
                      const nextReference = checked ? vendorPaymentReference : '';
                      setVendorPaid(checked);
                      setVendorPaidDate(nextDate);
                      if (!checked) {
                        setVendorPaymentReference('');
                      }
                      handleVendorPaidSave({
                        vendorPaid: checked,
                        vendorPaidDate: nextDate,
                        vendorPaymentReference: checked ? nextReference : null,
                      });
                    }}
          onVendorPaidDateChange={(date) => {
                      setVendorPaidDate(date);
                      if (vendorPaid) {
                        handleVendorPaidSave({
                          vendorPaid: true,
                          vendorPaidDate: date,
                          vendorPaymentReference,
                        });
                      }
                    }}
          onVendorPaymentReferenceChange={(reference) => {
                      setVendorPaymentReference(reference);
                      if (vendorPaid) {
                        handleVendorPaidSave({
                          vendorPaid: true,
                          vendorPaidDate,
                          vendorPaymentReference: reference,
                        });
                      }
                    }}
          onNteChange={(amount) => {
            updateNteMutation.mutate(amount);
          }}
          onCostsChange={(costs) => {
            updateCostsMutation.mutate(costs);
          }}
          documentationChecklist={workOrder.documentationChecklist}
        />
      </div>

      {/* Add Entry Modal */}
      {isAddEntryModalOpen && (
        <Modal
          isOpen={isAddEntryModalOpen}
          onClose={() => {
            if (isSubmitting) return;
            setIsAddEntryModalOpen(false);
          }}
          title="Add Timeline Entry"
          maxWidth="4xl"
          closeOnBackdropClick={false}
        >
          <TimelineEntryForm
            entryTypeId={newEntryTypeId}
            setEntryTypeId={setNewEntryTypeId}
            isLoading={isSubmitting}
            uploadProgress={uploadProgress}
            isModal
            currentStatusName={workOrder?.statusName}
            currentStatusId={workOrder?.statusId}
            onCancel={() => {
              if (isSubmitting) return;
              setIsAddEntryModalOpen(false);
              setNewMessage('');
              setAttachedFiles([]);
              setSelectedVendorId('');
            }}
            onSubmit={async (entryTypeId, metadata, message, attachments) => {
              if (isSubmitting) return;
              setIsSubmitting(true);
              setUploadProgress(attachments && attachments.length > 0 ? 0 : null);
              try {
                const workOrderId = parseInt(id!);
                
                // Use specific API methods for entry types with metadata
                switch (entryTypeId) {
                  case 2: // Call
                    console.log('Creating call entry with metadata:', metadata);
                    await apiService.createCallEntry(workOrderId, metadata, message);
                    // Invalidate queries to refresh timeline
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    // Close modal and reset form
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Timeline entry added successfully');
                    break;
                  case 5: // Include on Invoice
                    await apiService.createIncludeOnInvoiceEntry(workOrderId, metadata, message);
                    // Invalidate queries to refresh timeline
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    // Close modal and reset form
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Timeline entry added successfully');
                    break;
                  case 6: // Status Update
                    await apiService.createStatusUpdateEntry(workOrderId, metadata, message);
                    // Invalidate queries to refresh timeline
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    // Close modal and reset form
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Timeline entry added successfully');
                    break;
                  case 7: // Vendor Message
                    await apiService.createVendorMessageEntry(workOrderId, metadata, message);
                    // Invalidate queries to refresh timeline
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    // Close modal and reset form
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Timeline entry added successfully');
                    break;
                  case 8: // Client Message
                    await apiService.createClientMessageEntry(workOrderId, metadata, message);
                    // Invalidate queries to refresh timeline
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    // Close modal and reset form
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Timeline entry added successfully');
                    break;
                  case 9: // System Message
                    await apiService.createSystemMessageEntry(workOrderId, metadata, message);
                    // Invalidate queries to refresh timeline
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    // Close modal and reset form
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Timeline entry added successfully');
                    break;
                  case 10: // Verbal Authorization
                    await apiService.createVerbalAuthorizationEntry(workOrderId, metadata, message);
                    queryClient.invalidateQueries({ queryKey: ['contactEntries', id] });
                    setIsAddEntryModalOpen(false);
                    setNewMessage('');
                    setAttachedFiles([]);
                    setSelectedVendorId('');
                    toast.success('Verbal authorization logged');
                    break;
                  case 4: // File Upload - use generic method to support attachments
                  default:
                    // Use the generic method for file uploads and other entry types
                    let fileUploadMetadata = undefined;
                    if (entryTypeId === 4 && attachments && attachments.length > 0) {
                      // Create proper FileUploadMetadata for each file
                      const fileMetadata = attachments.map(file => ({
                        fileName: file.name,
                        fileType: file.type || 'unknown',
                        fileSizeBytes: file.size,
                        description: metadata.description || '',
                        isConfidential: metadata.isConfidential || false
                      }));
                      fileUploadMetadata = JSON.stringify(fileMetadata);
                    }
                    
                    const formData: CreateJobContactEntryForm = {
                      entryTypeId,
                      message,
                      metadata: fileUploadMetadata || (metadata ? JSON.stringify(metadata) : undefined),
                      attachments,
                      vendorId: selectedVendorId ? parseInt(selectedVendorId) : undefined
                    };
                    await createEntryMutation.mutateAsync({
                      data: formData,
                      onProgress: (percent) => setUploadProgress(percent),
                    });
                }
                
                // Post-create actions handled in mutation onSuccess to avoid duplicate toasts
                // Modal closing and form reset are handled in each case above
              } catch (error) {
                console.error('Error creating contact entry:', error);
                toast.error(getApiErrorMessage(error, 'Failed to add timeline entry'));
              } finally {
                setIsSubmitting(false);
                setUploadProgress(null);
              }
            }}
            entryTypes={entryTypes}
            vendors={workOrder?.vendors || []}
            defaultVendorId={defaultVendorId}
          />
        </Modal>
      )}

      {/* Delete Attachment Modal */}
      <DeleteAttachmentModal
        isOpen={isDeleteAttachmentOpen}
        onClose={() => {
          setIsDeleteAttachmentOpen(false);
          setAttachmentPendingDelete(null);
        }}
        attachment={attachmentPendingDelete}
        onConfirm={(attachmentId, deleteReason) => {
          deleteAttachmentMutation.mutate({ attachmentId, deleteReason });
        }}
        isLoading={deleteAttachmentMutation.isPending}
      />

      {/* Image / video preview modal */}
      {imageModalOpen && currentImageUrl && (
        <Modal
          isOpen={imageModalOpen}
          onClose={closeImageModal}
          title={currentImageName}
          maxWidth="6xl"
        >
          <div className="flex justify-center items-center h-[calc(100vh-10rem)]">
            {previewKind === 'video' ? (
              <video
                src={currentImageUrl}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-[calc(100vh-12rem)] rounded-lg shadow-lg bg-black"
                onError={() => toast.error('This video cannot play in the browser. Use Download instead.')}
              >
                Your browser cannot play this video.
              </video>
            ) : (
              <img
                src={currentImageUrl}
                alt={currentImageName}
                className="max-w-full max-h-[calc(100vh-12rem)] object-contain rounded-lg shadow-lg"
              />
            )}
          </div>
        </Modal>
      )}

      {/* Edit Timeline Entry Modal */}
      {isEditEntryOpen && entryToEdit && (
        <EditTimelineEntryModal
          isOpen={isEditEntryOpen}
          onClose={() => {
            setIsEditEntryOpen(false);
            setEntryToEdit(null);
          }}
          entry={entryToEdit}
          entryTypes={entryTypes}
          onSave={(entryId, data) => updateEntryMutation.mutate({ entryId, data })}
          isLoading={updateEntryMutation.isPending}
        />
      )}

      {/* History Modal */}
      {isHistoryModalOpen && selectedEntryForHistory && (
        <TimelineEntryHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => {
            setIsHistoryModalOpen(false);
            setSelectedEntryForHistory(null);
          }}
          entry={selectedEntryForHistory}
          history={entryHistory?.data}
        />
      )}

      {/* Invoice Details Modal */}
      {showInvoiceDetails && selectedInvoice && (
        <Modal
          isOpen={showInvoiceDetails}
          onClose={() => {
            setShowInvoiceDetails(false);
            setSelectedInvoice(null);
          }}
          title={`Invoice ${selectedInvoice.invoiceNumber}`}
          maxWidth="4xl"
        >
          <div className="space-y-6">
            {/* Invoice Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Invoice Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Invoice Number:</span>
                    <span className="font-medium">{selectedInvoice.invoiceNumber}</span>
                    </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date:</span>
                    <span>{new Date(selectedInvoice.invoiceDate).toLocaleDateString()}</span>
                </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Due Date:</span>
                    <span>{new Date(selectedInvoice.dueDate).toLocaleDateString()}</span>
                    </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedInvoice.status === 'draft' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                      selectedInvoice.status === 'sent' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      selectedInvoice.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                      {selectedInvoice.status}
                      </span>
                    </div>
                  {selectedInvoice.sentAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Sent:</span>
                      <span>{new Date(selectedInvoice.sentAt).toLocaleDateString()} at {new Date(selectedInvoice.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}
                  {selectedInvoice.paidAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Paid:</span>
                      <span>{new Date(selectedInvoice.paidAt).toLocaleDateString()} at {new Date(selectedInvoice.paidAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                          )}
                        </div>
                      </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Client Information</h3>
                <div className="space-y-2">
                  <div className="font-medium">{selectedInvoice.clientName}</div>
                  {selectedInvoice.clientAddress && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedInvoice.clientAddress}
                      </div>
                    )}
                  {(selectedInvoice.clientCity || selectedInvoice.clientState || selectedInvoice.clientZipCode) && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {[selectedInvoice.clientCity, selectedInvoice.clientState, selectedInvoice.clientZipCode]
                        .filter(Boolean)
                        .join(', ')}
                      </div>
                    )}
                  {selectedInvoice.clientEmail && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedInvoice.clientEmail}
                        </div>
                  )}
                  {selectedInvoice.clientPhone && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedInvoice.clientPhone}
                            </div>
                          )}
                            </div>
                            </div>
                        </div>

            {/* Invoice Items */}
            {selectedInvoice.items && selectedInvoice.items.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Items</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <div className="col-span-6">Description</div>
                      <div className="col-span-2 text-right">Qty</div>
                      <div className="col-span-2 text-right">Rate</div>
                      <div className="col-span-2 text-right">Amount</div>
                  </div>
                      </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {selectedInvoice.items.map((item: any, index: number) => (
                      <div key={index} className="px-4 py-3">
                        <div className="grid grid-cols-12 gap-4 text-sm">
                          <div className="col-span-6">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{item.description}</div>
                            {item.notes && (
                              <div className="text-gray-600 dark:text-gray-400 mt-1">{item.notes}</div>
                                  )}
                                </div>
                          <div className="col-span-2 text-right text-gray-900 dark:text-gray-100">
                            {item.quantity}
                              </div>
                          <div className="col-span-2 text-right text-gray-900 dark:text-gray-100">
                            ${item.unitPrice?.toFixed(2) || '0.00'}
                                </div>
                          <div className="col-span-2 text-right font-medium text-gray-900 dark:text-gray-100">
                            ${item.total?.toFixed(2) || '0.00'}
                                </div>
                                </div>
                                  </div>
                    ))}
                                      </div>
                                      </div>
                      </div>
                    )}
                    
            {/* Invoice Totals */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                    <span>${selectedInvoice.subtotal?.toFixed(2) || '0.00'}</span>
                        </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Tax ({(selectedInvoice.taxRate * 100)?.toFixed(1) || '0.0'}%):</span>
                    <span>${selectedInvoice.taxAmount?.toFixed(2) || '0.00'}</span>
                      </div>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <span>${selectedInvoice.total?.toFixed(2) || '0.00'}</span>
            </div>
        </div>
      </div>
        </div>
          </div>
          
            {/* Notes */}
            {selectedInvoice.notes && (
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Notes</h3>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedInvoice.notes}</p>
            </div>
              </div>
            )}
          
            {/* Terms */}
            {selectedInvoice.terms && (
          <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Terms</h3>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedInvoice.terms}</p>
          </div>
                </div>
              )}
        
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          {selectedInvoice.status !== 'paid' && (
            <button
              onClick={() => {
                setShowInvoiceDetails(false);
                setSelectedInvoice(null);
                navigate(`/invoices/${selectedInvoice.id}/edit`);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Edit Invoice
            </button>
          )}
              {selectedInvoice.status === 'draft' && (
          <button
                  onClick={() => markSentMutation.mutate(selectedInvoice.id)}
                  disabled={markSentMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {markSentMutation.isPending ? 'Sending...' : 'Mark as Sent'}
          </button>
              )}
              {selectedInvoice.status === 'sent' && (
            <button
                  onClick={() => markPaidMutation.mutate(selectedInvoice.id)}
                  disabled={markPaidMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                  {markPaidMutation.isPending ? 'Processing...' : 'Mark as Paid'}
            </button>
              )}
          </div>
        </div>
      </Modal>
      )}

      {/* Email Work Order Modal */}
      {workOrder && (
        <EmailWorkOrderModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          workOrder={workOrder}
        />
      )}

      <DocumentationOverrideModal
        isOpen={showDocumentationOverrideModal}
        onClose={() => {
          setShowDocumentationOverrideModal(false);
          setDocumentationWarning(null);
          setPendingStatusId(null);
        }}
        checklist={documentationWarning}
        onConfirm={handleDocumentationOverrideConfirm}
        isLoading={updateStatusMutation.isPending}
      />
    </div>
  );
};

export default WorkOrderContactPage;
